import { createFieldBoundaryWorker } from 'app/utils/field-boundary/create-worker';
import { isPositiveNumber } from 'app/utils/field-boundary/shared';
import {
  FieldBoundarySuggestion,
  FieldBoundarySuggestionTask,
} from 'app/utils/field-boundary/types';
import {
  createFieldBoundaryWorkerRequest,
  FieldBoundaryWorkerResponse,
  handleFieldBoundaryWorkerRequest,
} from 'app/utils/field-boundary/worker-protocol';

/** Name of the error raised when the caller aborts, which is not a failure. */
export const FIELD_BOUNDARY_ABORT_ERROR_NAME = 'AbortError';

/** Name of the error raised when the worker takes longer than allowed. */
export const FIELD_BOUNDARY_TIMEOUT_ERROR_NAME = 'FieldBoundaryTimeoutError';

export interface BackgroundSuggestionOptions {
  /** Aborting stops the worker and rejects with `FIELD_BOUNDARY_ABORT_ERROR_NAME`. */
  signal?: AbortSignal;
  /** The worker is stopped after this long, rejecting with `FIELD_BOUNDARY_TIMEOUT_ERROR_NAME`. */
  timeoutMs?: number;
}

type WorkerOutcome =
  | { ranInWorker: true; suggestion: FieldBoundarySuggestion | null }
  | { ranInWorker: false };

function namedError(name: string): Error {
  const error = new Error(`Field boundary suggestion failed (${name})`);
  error.name = name;
  return error;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw namedError(FIELD_BOUNDARY_ABORT_ERROR_NAME);
  }
}

function settle(
  response: FieldBoundaryWorkerResponse,
): FieldBoundarySuggestion | null {
  if ('errorName' in response) {
    throw namedError(response.errorName);
  }
  return response.suggestion;
}

/**
 * Runs the suggestion in the worker. Resolves with `ranInWorker: false` when
 * the worker could not run it at all, e.g. its script failed to load (a chunk
 * removed by a deploy after the page was opened).
 */
function runInWorker(
  worker: Worker,
  task: FieldBoundarySuggestionTask,
  { signal, timeoutMs }: BackgroundSuggestionOptions,
): Promise<WorkerOutcome> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (complete: () => void) => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      worker.terminate();
      complete();
    };
    const onAbort = () =>
      finish(() => reject(namedError(FIELD_BOUNDARY_ABORT_ERROR_NAME)));

    signal?.addEventListener('abort', onAbort);
    if (isPositiveNumber(timeoutMs)) {
      timer = setTimeout(
        () =>
          finish(() => reject(namedError(FIELD_BOUNDARY_TIMEOUT_ERROR_NAME))),
        timeoutMs,
      );
    }
    worker.onmessage = ({ data }: MessageEvent<FieldBoundaryWorkerResponse>) =>
      finish(() => {
        try {
          resolve({ ranInWorker: true, suggestion: settle(data) });
        } catch (error) {
          reject(error);
        }
      });
    worker.onerror = (event: ErrorEvent) => {
      event.preventDefault();
      finish(() => resolve({ ranInWorker: false }));
    };

    const request = createFieldBoundaryWorkerRequest(task);
    worker.postMessage(request, [
      request.coverage.coordinates.buffer,
      request.coverage.offsets.buffer,
    ]);
  });
}

/**
 * Runs the suggestion on the main thread, after yielding once so a spinner
 * shown just before gets painted. Builds its own request: the one posted to a
 * failed worker had its buffers transferred away.
 */
async function runInPlace(
  task: FieldBoundarySuggestionTask,
  signal: AbortSignal | undefined,
): Promise<FieldBoundarySuggestion | null> {
  await new Promise((resolve) => setTimeout(resolve));
  throwIfAborted(signal);
  return settle(
    handleFieldBoundaryWorkerRequest(createFieldBoundaryWorkerRequest(task)),
  );
}

/**
 * Suggests a field boundary off the main thread: the heavy work runs in a Web
 * Worker, and only collecting and packing the trails stays on the main thread.
 * Runs in place where workers are unavailable or fail to start.
 *
 * Rejects when the suggestion fails, when the worker times out, and when the
 * caller aborts; errors carry only a name, never task data.
 */
export async function suggestFieldBoundaryInBackground(
  task: FieldBoundarySuggestionTask,
  options: BackgroundSuggestionOptions = {},
): Promise<FieldBoundarySuggestion | null> {
  throwIfAborted(options.signal);
  const worker = createFieldBoundaryWorker();
  if (worker) {
    const outcome = await runInWorker(worker, task, options);
    if (outcome.ranInWorker) {
      return outcome.suggestion;
    }
  }
  return runInPlace(task, options.signal);
}

export function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error && error.name === FIELD_BOUNDARY_ABORT_ERROR_NAME
  );
}
